import React, { useState, useEffect, useRef } from "react";
import styles from "../styles/pages/Blog.module.css";
import { BlogPost, Pagination, TableOfContents } from "../components";
import { FallbackMessage } from "../components/blog/FallbackMessage";
import { fetchBlogPosts } from "../utils/blogUtils";
import { extractPostIdFromUrl } from "../utils/urlUtils";

/**
 * Blog page component with hash router URL support
 * Handles both /#/blog and /#/blog#post-[id] URL formats
 */
export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const POSTS_PER_PAGE = 5;
  const initialLoadComplete = useRef(false);

  // Handle blog post loading with hash router URL support
  useEffect(() => {
    // Extract post ID from URL considering hash router format
    const postId = extractPostIdFromUrl();
    
    const loadPosts = async () => {
      try {
        setLoading(true);
        
        const data = await fetchBlogPosts();
        
        if (data && data.length > 0) {
          setPosts(data);
          setError(null);
          
          // Handle scrolling to specific post from URL or session storage
          const targetPostId = postId || sessionStorage.getItem('lastViewedPost');
          
          if (targetPostId) {
            // Clear storage to prevent recurring scrolls
            sessionStorage.removeItem('lastViewedPost');
            
            const postIndex = data.findIndex(post => post.id === targetPostId);
            
            if (postIndex !== -1) {
              // Calculate which page contains this post
              const targetPage = Math.floor(postIndex / POSTS_PER_PAGE);
              setCurrentPage(targetPage);
              
              // After posts render, scroll to the specific post
              setTimeout(() => {
                const targetElement = document.getElementById(`post-${targetPostId}`);
                if (targetElement) {
                  targetElement.scrollIntoView({ behavior: 'smooth' });
                }
              }, 500);
            }
          }
        } else {
          setError("No blog posts were found.");
        }
      } catch (err) {
        console.error("Error loading blog posts:", err);
        setError(`Failed to load blog posts: ${err.message}`);
      } finally {
        setLoading(false);
        initialLoadComplete.current = true;
      }
    };
    
    // Load posts on initial mount or when retry is attempted
    loadPosts();
    
    // Add hash change listener for hash router navigation
    const handleHashChange = () => {
      if (initialLoadComplete.current && posts.length > 0) {
        // Extract post ID from URL considering hash router format
        const newPostId = extractPostIdFromUrl();
        
        if (newPostId) {
          const postIndex = posts.findIndex(post => post.id === newPostId);
          
          if (postIndex !== -1) {
            const targetPage = Math.floor(postIndex / POSTS_PER_PAGE);
            setCurrentPage(targetPage);
            
            setTimeout(() => {
              const targetElement = document.getElementById(`post-${newPostId}`);
              if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }
        }
      }
    };

    // Use hashchange event instead of popstate for hash router
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [loadAttempts]); // Re-run effect when loadAttempts changes

  // Calculate pagination values
  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const visiblePosts = posts.slice(
    currentPage * POSTS_PER_PAGE,
    (currentPage + 1) * POSTS_PER_PAGE
  );

  // Handlers for navigating between pages
  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle reload attempt on error with retry counter
  const handleRetry = () => {
    setLoadAttempts(prev => prev + 1);
    setLoading(true);
    setError(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.blogLayout}>
        <div className={styles.mainContent}>
          {loading ? (
            <div className={styles.loadingState}>
              <p>Loading blog posts...</p>
              <div className={styles.loadingSpinner}></div>
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <FallbackMessage error={error} />
              <button className={styles.retryButton} onClick={handleRetry}>
                Retry Loading Posts
              </button>
            </div>
          ) : visiblePosts.length > 0 ? (
            <>
              {visiblePosts.map((post) => (
                <BlogPost
                  key={post.id}
                  id={post.id}
                  title={post.title}
                  date={post.date}
                  description={post.description}
                  tags={post.tags}
                  content={post.content}
                />
              ))}
              
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrevPage={handlePrev}
                  onNextPage={handleNext}
                />
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>No blog posts available at this time. Check back soon!</p>
              <button className={styles.retryButton} onClick={handleRetry}>
                Refresh
              </button>
            </div>
          )}
        </div>
        
        {posts.length > 0 && <TableOfContents posts={posts} />}
      </div>
    </div>
  );
};

export default Blog;
